<!-- eslint-disable max-len -->
<template>
  <div>
    <b-modal
      id="workorderLineItemEditModal"
      size="lg"
      @ok="onWorkorderLineItemEditModalOk()"
    >
      <template v-slot:modal-title>Edit Work Order {{ workorderNumber }} Line Item</template>
      <b-card-body
        v-if='totalAvailableExceeded'
        class='project-workorder-lineitem-edit-card-warning text-danger'
      >
        <span class='project-workorder-bills-items-details-label'>This line item exceeds amount remaining for the funding source selected.&nbsp;</span>
        <span>Please adjust the amount.</span>
      </b-card-body>
      <b-card-body class='project-workorder-lineitem-edit-card-body'>
        <b-row>
          <b-col>
            <span class='details-label project-workorder-bills-items-details-label'>Budgeted: </span>
            <span class='project-workorder-bills-items-details-label'>
              <!-- {{ totalBudgeted }} -->
              {{ totalBudgeted | toCurrency }}
            </span>
          </b-col>
          <b-col>
            <span class='details-label project-workorder-bills-items-details-label'>Obligated: </span>
            <span class='project-workorder-bills-items-details-label'>
              <!-- {{ totalObligated }} -->
              {{ totalObligated | toCurrency }}
            </span>
          </b-col>
          <b-col>
            <span class='details-label project-workorder-bills-items-details-label'>Available: </span>
            <span v-bind:class="{'project-workorder-bills-items-details-label': true, 'text-danger': totalAvailable < 0}">
              <!-- {{ totalAvailable }} -->
              {{ totalAvailable | toCurrency }}
            </span>
          </b-col>
        </b-row>
      </b-card-body>
      <VueFormGenerator
        :model="workorderLineItemEditModel"
        :schema="workorderLineItemEditSchema"
        :options="{
          validateAfterLoad: false,
          validateAfterChanged: true,
          validateAsync: true
        }"
        @model-updated='onWorkorderLineItemEditModelUpdated'
        @validated="onWorkorderLineItemEditModelValidated"
      />
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>Cancel</b-button>
        <b-button size="sm" variant="success" @click="ok" class='btn-success' :disabled='!workorderLineItemEditModelValid'>Save</b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
/* eslint-disable max-len, no-restricted-properties, padded-blocks, prefer-destructuring */
import { mapState } from 'vuex';
import ProjectSchemaHelper from '@/mixins/projectSchemaHelper';

export default {
  name: 'ProjectWorkorderLineItemEdit',
  mixins: [
    ProjectSchemaHelper,
  ],
  computed: {
    ...mapState('Pricelist', {
      pricelistEntries: (e) => e.pricelistEntries,
    }),
    ...mapState('Project', {
      products: (state) => state.products,
      project: (state) => state.project,
      projectBudget: (state) => state.projectBudget,
    }),
    ...mapState('Workorder', {
      workorderLineItemEditModel: (e) => e.workorderLineItemEditModel,
      workorderLineItemEditedModel: (e) => e.workorderLineItemEditedModel,
    }),
    totalAvailableExceeded() {
      if (!this.workorderLineItemEditModel) return false;

      const originalDistrictPartnerCost = this.workorderLineItemEditModel.originalDistrictPartnerCost;

      const totalAvailable = Math.round(this.totalAvailable * 100);

      const itemCost = Math.round(this.workorderLineItemEditModel.quantity * (this.workorderLineItemEditModel.unitCost * 100));

      const districtPartnerCost = itemCost * (this.workorderLineItemEditModel.districtAndPartnerCost * 0.01);

      const totalAvailableExceeded = (totalAvailable + originalDistrictPartnerCost) < districtPartnerCost;

      return totalAvailableExceeded;
    },
  },
  data() {
    return {
      totalBudgeted: 0,
      totalObligated: 0,
      totalAvailable: 0,
      workorderLineItemEditFundingSources: [],
      workorderLineItemEditModelValid: false,
      workorderLineItemEditSchema: null,
      workorderLineItemPricelistEntries: [],
      workorderNumber: null,
      billingComplete: false,
      districtAndPartnerCost: 0,
      districtAndPartnerCostValue: 0,
    };
  },
  async mounted() {
    this.totalBudgeted = this.workorderLineItemEditModel.totalBudgeted;
    this.totalObligated = this.workorderLineItemEditModel.totalObligated;
    this.totalAvailable = (Math.round(this.totalBudgeted * 100) - Math.round(this.totalObligated * 100)) / Math.pow(10, 2);

    this.workorderNumber = this.workorderLineItemEditModel.workorderNumber;

    this.workorderLineItemEditFundingSources = this.workorderLineItemEditModel.fundingSources;

    this.billingComplete = this.workorderLineItemEditModel.billingComplete;

    this.districtAndPartnerCost = this.workorderLineItemEditModel.districtAndPartnerCost;

    this.workorderLineItemPricelistEntries = this.pricelistEntries.map((e) => {
      const workorderLineItemPricelistEntry = {
        id: e.id,
        price: e.price,
      };
      const product = this.products.find((p) => p.producT_ID === e.productId && p.active === true);
      if (product) {
        workorderLineItemPricelistEntry.name = product.producT_NAME;
        workorderLineItemPricelistEntry.unit = product.customfields.find((customfield) => customfield.fielD_NAME === 'Unit__c').fielD_VALUE;
      }
      return workorderLineItemPricelistEntry;
    }).filter((e) => e.name && e.name != null).sort(
      (a, b) => a.name.localeCompare(b.name),
    );

    this.validateWorkOrderLineItemEditFields([]);

    this.workorderLineItemEditSchema = this.getProjectWorkorderLineItemEditSchema();

    this.$bvModal.show('workorderLineItemEditModal');
  },
  methods: {
    async onWorkorderLineItemEditModalOk() {
      await this.$store.dispatch('Workorder/setFormLoading', true);
      await this.$store.dispatch('Workorder/putWorkorderLineItem', this.workorderLineItemEditModel);
      await this.$store.dispatch('Workorder/setFormLoading', false);
    },
    onWorkorderLineItemEditModelUpdated(fieldValue, fieldName) {
      if (fieldName === 'pricebookEntryId') {
        const pricelistEntry = this.pricelistEntries.find((e) => Number(e.id) === Number(fieldValue));
        this.workorderLineItemEditModel.costItemId = pricelistEntry.productId;
        this.workorderLineItemEditModel.unitCost = pricelistEntry.price;
      }
      this.workorderLineItemEditModel.itemCost = this.$options.filters.toCurrency(
        this.workorderLineItemEditModel.unitCost * this.workorderLineItemEditModel.quantity,
      );

      const distPartnerCost = this.workorderLineItemEditModel.unitCost * this.workorderLineItemEditModel.quantity * this.workorderLineItemEditModel.districtAndPartnerCost * 0.01;
      let distPartnerCostRounded = Math.round(distPartnerCost * 100);

      /* eslint-disable-next-line */
      distPartnerCostRounded /= Math.pow(10, 2);

      this.workorderLineItemEditModel.districtAndPartnerCostValue = distPartnerCostRounded;
    },
    onWorkorderLineItemEditModelValidated(isValid, errors) {
      this.workorderLineItemEditModelValid = isValid;
      this.validateWorkOrderLineItemEditFields(errors);
    },
    validateWorkOrderLineItemEditFields(errors) {
      // console.warn('Enter validateWorkOrderLineItemEditFields()');

      this.workorderLineItemEditModelValid = true;

      if (!this.workorderLineItemEditModel.pricebookEntryId || this.workorderLineItemEditModel.pricebookEntryId === null) {
        // console.warn('Invalid priceBookEntryId');
        this.workorderLineItemEditModelValid = false;
      }

      if (!this.workorderLineItemEditModel.unitCost || this.workorderLineItemEditModel.unitCost === null) {
        // console.warn('Invalid unitCost');
        this.workorderLineItemEditModelValid = false;
      }

      if (!this.workorderLineItemEditModel.quantity || this.workorderLineItemEditModel.quantity === null) {
        // console.warn('Invalid quantity');
        this.workorderLineItemEditModelValid = false;
      }

      if (!this.workorderLineItemEditModel.fundingSourceId || this.workorderLineItemEditModel.fundingSourceId === null) {
        // console.warn('Invalid fundingSourceId');
        this.workorderLineItemEditModelValid = false;
      }

      if (this.totalAvailableExceeded) {
        // console.warn('totalAvailableExceeded === true');
        this.workorderLineItemEditModelValid = false;
      }

      if (errors && errors.length > 0) {
        // console.warn('errors: ', errors);
        this.workorderLineItemEditModelValid = false;
      }

      // console.warn('Exit validateWorkOrderLineItemEditFields()');
    },
  },
};
</script>
